/**
 * FRE (First Run Experience) API Client
 * Provides API methods for managing user FRE state
 */

import { IPublicClientApplication, AccountInfo, SilentRequest } from '@azure/msal-browser';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { apiConfig, getLoginRequest, getAuthProvider, isTestMode } from '../../config';
import { appInsights } from '../../telemetry/appInsights';

export interface ShouldShowFREResponse {
  shouldShowFRE: boolean;
  email: string;
}

export interface UserFREDto {
  email: string;
  wasFREShown: boolean;
  timestamp?: string;
}

/**
 * Gets the authorization token for API calls
 */
const getAuthToken = async (
  msalInstance: IPublicClientApplication,
  account: AccountInfo
): Promise<string> => {
  const authProvider = getAuthProvider();
  const loginReq = getLoginRequest(authProvider);
  const apiScopes = loginReq.scopes && loginReq.scopes.length > 0 ? loginReq.scopes : [];

  if (apiScopes.length === 0) {
    const idTokenResponse = await msalInstance.acquireTokenSilent({
      account: account,
      scopes: ['openid', 'profile'],
    });
    return idTokenResponse.idToken;
  }

  const silentRequest: SilentRequest = {
    scopes: apiScopes,
    account: account,
    forceRefresh: false,
  };

  const response = await msalInstance.acquireTokenSilent(silentRequest);

  const token = (response.accessToken || response.idToken || '').trim();
  if (!token) {
    throw new Error('Failed to acquire auth token (accessToken and idToken are empty).');
  }

  return token;
};

/**
 * FRE API Client class
 */
export class FREApiClient {
  private msalInstance: IPublicClientApplication | null;
  private account: AccountInfo | null;
  private baseUrl: string;

  constructor(msalInstance?: IPublicClientApplication | null, account?: AccountInfo | null) {
    this.msalInstance = msalInstance ?? null;
    this.account = account ?? null;
    this.baseUrl = apiConfig.baseUrl;
  }

  /**
   * Check if FRE should be shown to the current user
   */
  async shouldShowFRE(): Promise<ShouldShowFREResponse> {
    if (isTestMode || !this.msalInstance || !this.account) {
      // In test mode, always show FRE
      return { shouldShowFRE: true, email: 'test@test.com' };
    }

    try {
      const token = await getAuthToken(this.msalInstance, this.account);
      const response = await fetch(`${this.baseUrl}/api/UserFRE/should-show`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // On error, default to not showing FRE (per PR comment - don't constantly show FRE on errors)
        console.error('FRE API error:', response.status, response.statusText);
        appInsights.trackException({
          exception: new Error(`FRE API error: ${response.status}`),
          severityLevel: SeverityLevel.Warning,
          properties: {
            component: 'FREApiClient',
            action: 'shouldShowFRE',
            statusCode: String(response.status),
          },
        });
        return { shouldShowFRE: false, email: '' };
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to check FRE status:', error);
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(String(error)),
        severityLevel: SeverityLevel.Warning,
        properties: {
          component: 'FREApiClient',
          action: 'shouldShowFRE',
        },
      });
      // On error, default to not showing FRE
      return { shouldShowFRE: false, email: '' };
    }
  }

  /**
   * Acknowledge that the user has seen and closed the FRE
   */
  async acknowledgeFRE(): Promise<UserFREDto | null> {
    if (isTestMode || !this.msalInstance || !this.account) {
      return { email: 'test@test.com', wasFREShown: true };
    }

    try {
      const token = await getAuthToken(this.msalInstance, this.account);
      const response = await fetch(`${this.baseUrl}/api/UserFRE/acknowledge`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('FRE acknowledge error:', response.status, response.statusText);
        appInsights.trackException({
          exception: new Error(`FRE acknowledge error: ${response.status}`),
          severityLevel: SeverityLevel.Warning,
          properties: {
            component: 'FREApiClient',
            action: 'acknowledgeFRE',
            statusCode: String(response.status),
          },
        });
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to acknowledge FRE:', error);
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(String(error)),
        severityLevel: SeverityLevel.Warning,
        properties: {
          component: 'FREApiClient',
          action: 'acknowledgeFRE',
        },
      });
      return null;
    }
  }
}

/**
 * Creates a configured FRE API client instance
 */
export const createFREClient = (
  msalInstance?: IPublicClientApplication | null,
  account?: AccountInfo | null
): FREApiClient => {
  return new FREApiClient(msalInstance, account);
};
